import React, { useState } from "react";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import dayjs from "utils/dayjs";
import { formatCashFull } from "../../../utils/formatCash";
import TransactionStatus from "../../../enums/TransactionStatus";
import InvoiceDetailsDrawer from "components/admin/invoices/InvoiceDetailsDrawer";
import TransactionsType from "enums/TransactionsType";
import { GET_TRANSFER_DETAILS } from "graphql/transaction";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { useLazyQuery } from "@apollo/client";
import DocumentViewerDialog from "components/common/DocumentViewerDialog";
import DisputeTransactionDialog from "components/common/DisputeTransactionDialog";
import { canDisputeTransaction } from "utils/transactions/transactionActionGuards";
import Summary, { SummaryItem } from "../../common/Summary";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import ViewPaymentDetailsDrawer from "components/admin/payments/ViewPaymentDetailsDrawer";
import { RenderTitleCase } from "components/common/RenderTitleCase";
import AccountType from "enums/AccountType";
import PersonalExpenseButtons from "./PersonalExpenseButtons";
import { Avatar } from "@outpave/ds";
import Typography from "@mui/material/Typography";
import { Button } from "@mui/material";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { RenderTransactionStatus } from "components/common/RenderTransactionStatus";
import { RenderTransactionTypeChip } from "components/common/RenderTransactionType";
import { getDomain, logoLink } from "components/common/RenderMerchantDisplay";
import { fullName } from "utils/fullName";

interface DetailsProps {
  transaction: any;
  /** Manager and above: the account, the purchaser's profile, and the linked invoice and payment drawers. */
  adminTools?: boolean;
  handleOpenUserProfileDrawer: (user: any) => void;
  /** Called after an action changes the transaction. */
  handleRefresh?: () => void;
  loadingTransaction?: boolean;
}
const Details: React.FC<DetailsProps> = (props) => {
  const {
    transaction,
    adminTools = false,
    handleOpenUserProfileDrawer,
    handleRefresh,
    loadingTransaction = false,
  } = props;
  const { t } = useTranslation();

  const [loadingPersonalExpense, setLoadingPersonalExpense] = useState(false);

  // Linked Invoices summary items (if more than one, we group them)
  const invoices = Array.isArray(transaction?.linkedInvoices)
    ? transaction.linkedInvoices
    : [];
  const transactions = Array.isArray(transaction?.linkedTransactions)
    ? transaction.linkedTransactions
    : [];
  const payments = Array.isArray(transaction?.linkedPayments)
    ? transaction.linkedPayments
    : [];

  const isMultiInvoice = invoices.length > 1;

  const invoiceItems: SummaryItem[] = invoices.map((inv: any) => ({
    ...(isMultiInvoice
      ? {
          title: inv.invoiceName ?? t("Invoice"),
          value: inv.invoiceNumber ?? t("View Invoice"),
          groupBy: t("Invoices"),
        }
      : {
          title: t("Linked Invoice"),
          value: inv.invoiceName ?? t("Invoice"),
        }),
    onClick: adminTools ? () => invoiceDrawer.show(inv.id) : undefined,
  }));

  const transactionItems: SummaryItem[] = transactions.map((tx: any) => {
    return {
      title: tx.merchantData?.name || tx.merchant || t("Transaction"),
      value: tx.amount
        ? `${formatCashFull(tx.amount, tx.currency)}`
        : t("View Transaction"),
      groupBy: t("Linked Transactions"),
      onClick: () => transactionDrawer.show(tx.id),
    };
  });

  const isReceiptAvailable = () => {
    return (
      transaction?.owningAccount?.accountType ===
        AccountType.FINANCIAL_ACCOUNT &&
      [
        TransactionsType.INBOUND_TRANSFER,
        TransactionsType.OUTBOUND_TRANSFER,
      ].includes(transaction?.type) &&
      (transaction?.status === TransactionStatus.COMPLETE ||
        transaction?.status === TransactionStatus.FAILED)
    );
  };

  const isMultiPayment = payments.length > 1;
  const paymentItems: SummaryItem[] = payments.map((payment: any) => ({
    ...(isMultiPayment
      ? {
          title: t("Payment"),
          value: t("View Payment"),
          groupBy: t("Payments"),
        }
      : {
          title: t("Linked Payment"),
          value: t("Payment"),
        }),
    onClick: adminTools ? () => paymentDrawer.show(payment.id) : undefined,
  }));

  const linkedItems: SummaryItem[] = [
    ...invoiceItems,
    ...transactionItems,
    ...paymentItems,
  ];

  // The merchant, the amount and the status render above the grid now, in the
  // identity row and the hero figure, so they are deliberately absent here.
  const summaryItems: SummaryItem[] = [
    {
      title: t("Purchaser"),
      value: fullName(transaction?.user) || null,
      onClick:
        adminTools && transaction?.user?.id
          ? () => handleOpenUserProfileDrawer(transaction?.user?.id)
          : undefined,
    },
    {
      title: t("Type"),
      // The chip, matching the tables. Its label is the renderer's own, not a
      // title-cased enum: the raw value reads "Capture", where what the user
      // recognises is "Virtual Card".
      value: transaction?.type ? (
        <RenderTransactionTypeChip
          transactionType={transaction.type}
          cardType={transaction?.card?.type}
        />
      ) : null,
    },
    {
      title: t("Card used"),
      value: transaction?.card?.cardNumber
        ? `\u2022\u2022\u2022\u2022${transaction.card.cardNumber}`
        : null,
    },
    {
      title: t("Location"),
      value:
        transaction?.merchantData?.city && transaction?.merchantData?.state
          ? `${transaction.merchantData.city}, ${transaction.merchantData.state}`
          : null,
    },
    {
      title: t("Purchased"),
      value: transaction?.transactionDate
        ? dayjs(transaction.transactionDate).format("MM/DD/YYYY \u00b7 h:mm A")
        : null,
    },
    ...(adminTools
      ? [
          {
            title: t("Account"),
            fullWidth: true,
            value: (
              <RenderTitleCase
                text={[
                  transaction?.owningAccount?.name,
                  transaction.owningAccount?.last4,
                ]}
              />
            ),
          },
        ]
      : []),
    {
      title: t("Description"),
      fullWidth: true,
      value: (
        <RenderTitleCase text={transaction?.description || transaction?.memo} />
      ),
    },
    {
      title: t("Passenger Name"),
      value: transaction?.travelData?.airline?.passengerName,
    },
    {
      title: t("Record Locator"),
      value: transaction?.travelData?.airline?.recordLocator,
    },
    {
      title: t("Ticket Number"),
      value: transaction?.travelData?.airline?.ticketNumber,
    },
    ...(transaction?.fees?.feeAmount
      ? [
          {
            title: t("Processing Fee"),
            value: formatCashFull(
              transaction.fees.feeAmount,
              transaction?.currency,
            ),
          },
        ]
      : []),
    ...(transaction?.refundAmount && transaction?.refundAmount > 0
      ? [
          {
            title: t("refundFeature.refundAmount"),
            value: formatCashFull(
              transaction?.refundAmount,
              transaction?.currency,
            ),
          },
        ]
      : []),
    ...linkedItems,
    {
      title: t("Dispute Amount"),
      value: transaction?.disputeAmount
        ? formatCashFull(transaction?.disputeAmount, transaction?.currency)
        : null,
    },
    {
      title: t("Dispute Status"),
      value: <RenderTitleCase text={transaction?.disputeStatus} />,
    },
    {
      title: t("Receipt"),
      value: isReceiptAvailable() ? t("Click to view") : null,
      onClick: () => {
        handleOpenDetails(transaction.id);
      },
    },
    {
      title: t("Approval Status"),
      value: <RenderTitleCase text={transaction?.workflowRequest?.status} />,
    },
    {
      title: t("Reason"),
      fullWidth: true,
      value:
        transaction?.failureReason &&
        [TransactionStatus.DECLINED, TransactionStatus.REVERSED].includes(
          transaction?.status,
        )
          ? t(transaction.failureReason)
          : null,
    },
  ];

  const [receiptPopoverUrl, setReceiptPopoverUrl] = useState<string>("");
  const [openReceiptPopover, setOpenReceiptPopover] = useState<boolean>(false);
  const [openDisputeDialog, setOpenDisputeDialog] = useState<boolean>(false);

  const paymentDrawer = useDrawerRecord<string>();
  const invoiceDrawer = useDrawerRecord<string>();
  const transactionDrawer = useDrawerRecord<string>();

  const [getReceiptTransferDetails, { loading: loadingReceiptDetails }] =
    useLazyQuery(GET_TRANSFER_DETAILS, {
      onCompleted: (data) => {
        handleOpenReceiptPopover(data.getTransferDetails.receiptUrl);
      },
    });

  const handleOpenDetails = (id: string) => {
    getReceiptTransferDetails({ variables: { id: id } });
  };

  const handleOpenReceiptPopover = (url: string) => {
    setReceiptPopoverUrl(url);
    setOpenReceiptPopover(true);
  };

  const handleCloseReceiptPopover = () => {
    setReceiptPopoverUrl("");
    setOpenReceiptPopover(false);
  };

  const showDispute = !!transaction && canDisputeTransaction(transaction);

  const merchantName = transaction?.merchantData?.name || transaction?.merchant;
  // The grid used to DROP an absent merchant (Summary skips empty values); the
  // identity row always renders, so it needs something to render. The Avatar
  // keeps the raw value so a missing name falls through to its user glyph
  // rather than drawing an em dash as an initial.
  const merchantDisplay = merchantName || "\u2014";
  // Guard on the DOMAIN, not the raw url: `getDomain` returns "" for anything
  // it cannot parse, and an empty domain builds a Brandfetch link with a hole
  // in it. Without a logo the Avatar falls back to the merchant's initial.
  const merchantDomain = transaction?.merchantData?.url
    ? getDomain(transaction.merchantData.url)
    : "";
  const merchantLogo = merchantDomain
    ? logoLink(merchantDomain, "icon", "lettermark", "light", 64)
    : undefined;

  return (
    <Box>
      <Stack gap="var(--space-5)">
        <Inline justify="between" align="center" wrap={false}>
          <Inline align="center" gap="var(--space-3)" wrap={false}>
            <Avatar
              variant="rounded"
              size="lg"
              src={merchantLogo}
              name={merchantName}
              alt={merchantDisplay}
            />
            <Typography variant="h6" sx={{ flex: 1 }}>
              {merchantDisplay}
            </Typography>
          </Inline>
          <RenderTransactionStatus
            transactionStatus={transaction?.status}
            disputeStatus={transaction?.disputeStatus}
            workflowStatus={transaction?.workflowRequest?.status}
            transactionType={transaction?.type}
          />
        </Inline>

        <Typography variant="h2" component="p">
          {formatCashFull(transaction?.amount, transaction?.currency)}
        </Typography>

        <Summary
          disableContainer
          alignEndColumn
          items={summaryItems}
          loading={loadingTransaction}
          children={
            <Inline justify="end" gap="var(--space-2)">
              <PersonalExpenseButtons
                transaction={transaction}
                setLoadingPersonalExpense={setLoadingPersonalExpense}
                onStatusChange={handleRefresh}
              />
              {showDispute && (
                <Button
                  variant="default-outlined"
                  size="small"
                  onClick={() => setOpenDisputeDialog(true)}
                >
                  {t("Dispute")}
                </Button>
              )}
            </Inline>
          }
        />
      </Stack>

      <LoadingWithoutModal
        open={loadingReceiptDetails || loadingPersonalExpense}
      />

      <DocumentViewerDialog
        url={receiptPopoverUrl}
        open={openReceiptPopover}
        title={t("Transfer receipt")}
        onClose={handleCloseReceiptPopover}
      />

      {invoiceDrawer.record && (
        <InvoiceDetailsDrawer
          open={invoiceDrawer.open}
          invoiceId={invoiceDrawer.record}
          handleCloseInvoiceDetailsDrawer={invoiceDrawer.close}
          onAfterClose={invoiceDrawer.afterClose}
        />
      )}

      {transactionDrawer.record && (
        <TransactionDetailsDrawer
          open={transactionDrawer.open}
          handleCloseDrawer={transactionDrawer.close}
          onAfterClose={transactionDrawer.afterClose}
          transactionId={transactionDrawer.record}
        />
      )}

      {paymentDrawer.record && (
        <ViewPaymentDetailsDrawer
          open={paymentDrawer.open}
          paymentId={paymentDrawer.record}
          handleClose={paymentDrawer.close}
          onAfterClose={paymentDrawer.afterClose}
        />
      )}

      <DisputeTransactionDialog
        open={openDisputeDialog}
        transaction={
          transaction?.id
            ? {
                id: transaction.id,
                amount: transaction.amount,
                transactionDate: transaction.transactionDate,
                merchantName: transaction.merchant,
              }
            : undefined
        }
        onClose={() => setOpenDisputeDialog(false)}
        onCompleted={handleRefresh}
      />
    </Box>
  );
};

export default Details;
